import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  memo,
} from "react";
import Card from "../../../components/Global/Card";
import DataTable from "../../../components/Global/DataTable";
import Modal from "../../../components/Global/Modal";
import { CustomService } from "../../../services/api/custom/CustomService";
import { connect } from "react-redux";
import GoBack from "../../../components/Utils/GoBack";
import { LuFileSpreadsheet } from "react-icons/lu";
import { FaBoxOpen } from "react-icons/fa";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Tooltip } from "antd";

/**
 * Paginação simples (frontend-only)
 */
const Pagination = memo(function Pagination({
  page,
  totalPages,
  totalItems,
  pageSize,
  onPrev,
  onNext,
  onGoTo,
}) {
  if (totalItems === 0) return null;

  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);

  const getPageButtons = () => {
    const buttons = [];
    const windowSize = 2;
    const from = Math.max(1, page - windowSize);
    const to = Math.min(totalPages, page + windowSize);

    if (from > 1) {
      buttons.push(1);
      if (from > 2) buttons.push("...");
    }

    for (let p = from; p <= to; p++) buttons.push(p);

    if (to < totalPages) {
      if (to < totalPages - 1) buttons.push("...");
      buttons.push(totalPages);
    }

    return buttons;
  };

  return (
    <div className="flex flex-col gap-2 mt-3">
      <div className="text-xs text-gray-600">
        Mostrando {start}-{end} de {totalItems}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={onPrev}
          disabled={page <= 1}
          className="px-3 py-1 rounded text-xs border disabled:opacity-50"
        >
          Anterior
        </button>

        {getPageButtons().map((p, idx) =>
          p === "..." ? (
            <span key={`dots-${idx}`} className="px-2 text-xs text-gray-500">
              ...
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onGoTo(p)}
              className={`px-3 py-1 rounded text-xs border ${
                p === page ? "bg-gray-200 font-semibold" : ""
              }`}
            >
              {p}
            </button>
          )
        )}

        <button
          onClick={onNext}
          disabled={page >= totalPages}
          className="px-3 py-1 rounded text-xs border disabled:opacity-50"
        >
          Próxima
        </button>
      </div>
    </div>
  );
});

/**
 * Tabela (modo Interno: orçamentos)
 */
const OrcamentosTable = memo(function OrcamentosTable({
  data,
  loading,
  onOpenStatusModal,
  onEdit,
  onPdf,
}) {
  const columns = useMemo(() => {
    const formatDate = (date) =>
      date
        ? format(new Date(date), "dd/MM/yyyy HH:mm", { locale: ptBR })
        : null;

    const StatusButton = ({ field, active, color, label, record }) => (
      <Tooltip
        title={
          active
            ? `${label} em ${formatDate(record[`${field}_em`])}`
            : `Marcar como ${label}`
        }
      >
        <button
          onClick={() => onOpenStatusModal(record, field)}
          className={`px-2 py-[2px] rounded text-xs text-white transition
            ${active ? color : "bg-red-500 hover:bg-red-600"}
          `}
        >
          {active ? label : `Não ${label}`}
        </button>
      </Tooltip>
    );

    return [
      { title: "ID", dataIndex: "fid_pedido", key: "fid_pedido" },
      { title: "Cliente", dataIndex: "cliente", key: "cliente" },
      { title: "CNPJ", dataIndex: "cnpj", key: "cnpj" },
      { title: "Telefone", dataIndex: "telefone", key: "telefone" },
      {
        title: "Prazo Entrega",
        dataIndex: "prazo_entrega",
        key: "prazo_entrega",
      },
      {
        title: "Condição Pagamento",
        dataIndex: "cond_pagamento",
        key: "cond_pagamento",
      },
      { title: "Qtd Itens", dataIndex: "itens", key: "itens" },
      { title: "Valor Total", dataIndex: "preco_total", key: "preco_total" },
      { title: "Data", dataIndex: "criado_em", key: "criado_em" },
      {
        title: "Status",
        key: "status",
        render: (_, record) => (
          <div className="flex flex-col gap-[4px]">
            <StatusButton
              record={record}
              field="aprovado"
              label="Aprovado"
              active={!!record.aprovado_em}
              color="bg-green-500 hover:bg-green-600"
            />
            <StatusButton
              record={record}
              field="pago"
              label="Pago"
              active={!!record.pago_em}
              color="bg-yellow-500 hover:bg-yellow-600"
            />
            <StatusButton
              record={record}
              field="entregue"
              label="Entregue"
              active={!!record.entregue_em}
              color="bg-blue-500 hover:bg-blue-600"
            />
          </div>
        ),
      },
      {
        title: "Ações",
        key: "acoes",
        render: (_, record) => (
          <div className="flex flex-col gap-2">
            <button
              onClick={() => onPdf(record)}
              className="bg-green-500 hover:bg-green-600 text-white text-xs px-3 py-[4px] rounded"
            >
              PDF
            </button>
            <button
              onClick={() => onEdit(record)}
              className="bg-yellow-500 hover:bg-yellow-600 text-white text-xs px-3 py-[4px] rounded"
            >
              Editar
            </button>
          </div>
        ),
      },
    ];
  }, [onOpenStatusModal, onEdit, onPdf]);

  return (
    <DataTable size="100%" data={data} columns={columns} loading={loading} />
  );
});

/**
 * Tabela (modo Loja: pedidos)
 */
const PedidosLojaTable = memo(function PedidosLojaTable({
  data,
  loading,
  onDetails,
}) {
  const columns = useMemo(() => {
    return [
      { title: "ID", dataIndex: "fid_pedido", key: "fid_pedido" },
      { title: "Cliente", dataIndex: "cliente", key: "cliente" },
      { title: "CNPJ", dataIndex: "cnpj", key: "cnpj" },
      { title: "Telefone", dataIndex: "telefone", key: "telefone" },
      { title: "Qtd Itens", dataIndex: "itens", key: "itens" },
      { title: "Valor Total", dataIndex: "preco_total", key: "preco_total" },
      { title: "Data", dataIndex: "criado_em", key: "criado_em" },
      {
        title: "Status",
        key: "status",
        render: (_, record) => (
          <div className="flex flex-col gap-[4px]">
            <Tooltip
              title={
                record.status === "entregue"
                  ? "Pedido entregue"
                  : "Aguardando entrega"
              }
            >
              <span
                className={`px-2 py-[2px] rounded text-xs text-white cursor-default
                ${
                  record.status === "entregue"
                    ? "bg-blue-500"
                    : "bg-yellow-500"
                }`}
              >
                {record.status === "entregue" ? "Entregue" : "Em aberto"}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        title: "Ações",
        key: "acoes",
        render: (_, record) => (
          <div className="flex flex-col gap-2">
            <button
              onClick={() => onDetails(record)}
              className="bg-sky-500 hover:bg-sky-600 text-white text-xs px-3 py-[4px] rounded"
            >
              Ver Detalhes
            </button>
          </div>
        ),
      },
    ];
  }, [onDetails]);

  return (
    <DataTable size="100%" data={data} columns={columns} loading={loading} />
  );
});

const OrcamentosUnifiedPage = ({ isAuthenticated, user }) => {
  // origem: "interno" | "loja"
  const [source, setSource] = useState("interno");

  // dataset completo (para paginação frontend)
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(false);

  // modal status (só no modo interno)
  const [statusModal, setStatusModal] = useState({
    open: false,
    record: null,
    field: null,
  });
  const [statusLoading, setStatusLoading] = useState(false);

  // Paginação frontend
  const PAGE_SIZE = 30;
  const [page, setPage] = useState(1);

  // Cache em memória por origem (TTL)
  const cacheRef = useRef({
    interno: { data: null, fetchedAt: 0 },
    loja: { data: null, fetchedAt: 0 },
  });
  const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutos

  const formatResponseData = useCallback((resp) => {
    return resp.map((item) => ({
      ...item,
      preco_total: new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(item.preco_total / 100),
      criado_em: format(new Date(item.criado_em), "dd/MM/yyyy", {
        locale: ptBR,
      }),
    }));
  }, []);

  const fetchData = useCallback(
    async (opts = { force: false }) => {
      const modeKey = source === "loja" ? "loja" : "interno";
      const cached = cacheRef.current[modeKey];

      // usa cache se ainda está válido e não forçado
      if (
        !opts.force &&
        cached.data &&
        Date.now() - cached.fetchedAt < CACHE_TTL_MS
      ) {
        setAllData(cached.data);
        setPage(1);
        return;
      }

      setLoading(true);
      try {
        const url =
          source === "loja"
            ? "/interno/orcamentos?from_loja=true"
            : "/interno/orcamentos";

        const resp = await CustomService.getRequest(url);
        const newData = formatResponseData(resp);

        cacheRef.current[modeKey] = { data: newData, fetchedAt: Date.now() };
        setAllData(newData);
        setPage(1);
      } catch (error) {
        console.error(error);
        toast.error(
          source === "loja"
            ? "Erro ao buscar pedidos da loja!"
            : "Erro ao buscar orçamentos!"
        );
      } finally {
        setLoading(false);
      }
    },
    [source, formatResponseData]
  );

  // busca ao montar e ao trocar origem
  useEffect(() => {
    fetchData({ force: false });
  }, [fetchData]);

  // páginas
  const totalItems = allData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [page, totalPages]);

  const pagedData = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return allData.slice(start, end);
  }, [allData, page]);

  /* ---------------- status modal (interno) ---------------- */

  const openStatusModal = useCallback((record, field) => {
    setStatusModal({ open: true, record, field });
  }, []);

  const closeStatusModal = useCallback(() => {
    if (statusLoading) return;
    setStatusModal({ open: false, record: null, field: null });
  }, [statusLoading]);

  const toggleStatus = useCallback(async () => {
    const { record, field } = statusModal;
    if (!record || !field) return;

    setStatusLoading(true);
    try {
      const res = await CustomService.patchRequest(
        `/interno/orcamento/${record.id}/status`,
        { field }
      );

      // Atualiza no dataset completo
      setAllData((prev) =>
        prev.map((item) =>
          item.id === record.id
            ? { ...item, [`${field}_em`]: res.value }
            : item
        )
      );

      // Atualiza também no cache (interno)
      cacheRef.current.interno = {
        data: (cacheRef.current.interno.data || []).map((item) =>
          item.id === record.id
            ? { ...item, [`${field}_em`]: res.value }
            : item
        ),
        fetchedAt: cacheRef.current.interno.fetchedAt || Date.now(),
      };

      toast.success("Status atualizado com sucesso");
      closeStatusModal();
    } catch (err) {
      console.error(err);
      toast.error("Erro ao atualizar status");
    } finally {
      setStatusLoading(false);
    }
  }, [statusModal, closeStatusModal]);

  /* ---------------- ações ---------------- */

  const onEdit = useCallback((record) => {
    window.location.href = `/gerar-orcamento?id=${record.fid_pedido}`;
  }, []);

  const onPdfOrcamento = useCallback((record) => {
    const url = `https://api.foxsuprimentos.com.br/orcamento/${record.id}/file`;
    window.open(url, "_blank");
  }, []);

  const onDetailsLoja = useCallback((record) => {
    window.location.href = `/gerar-orcamento?id=${record.fid_pedido}`;
  }, []);

  /* ---------------- pagination handlers ---------------- */

  const goToPage = useCallback((p) => setPage(p), []);
  const prevPage = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);
  const nextPage = useCallback(
    () => setPage((p) => Math.min(totalPages, p + 1)),
    [totalPages]
  );

  /* ---------------- UI: filtro + refresh ---------------- */

  const onRefresh = useCallback(() => {
    fetchData({ force: true });
  }, [fetchData]);

  const onChangeSource = useCallback((e) => {
    setSource(e.target.value);
  }, []);

  const title = source === "loja" ? "Listagem de Pedidos" : "Listagem de Orçamentos";
  const icon = source === "loja" ? (
    <FaBoxOpen className="w-[25px] h-[25px]" />
  ) : (
    <LuFileSpreadsheet className="w-[25px] h-[25px]" />
  );

  return (
    <div className="flex flex-col w-full gap-3">
      <GoBack path="/dashboard" />

      {/* Modal só no modo interno */}
      {source === "interno" && statusModal.open && (
        <Modal
          title="Confirmar alteração de status"
          buttonName={statusLoading ? "Processando..." : "Confirmar"}
          handleModal={closeStatusModal}
          eventConfirmation={toggleStatus}
          size="md:w-1/3"
          disabled={statusLoading}
        >
          <div className="flex flex-col gap-2 items-center text-center">
            <span>
              Deseja alterar o status <b>{statusModal.field}</b> do orçamento{" "}
              <b>{statusModal.record?.fid_pedido}</b>?
            </span>

            {statusLoading && (
              <span className="text-sm text-gray-500">Aguarde...</span>
            )}
          </div>
        </Modal>
      )}

      <Card
        buttonActionPath={source === "interno" ? "/gerar-orcamento" : ""}
        title={title}
        icon={icon}
      >
        {/* Toolbar */}
        <div className="w-full flex flex-col gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-sm text-gray-700">Origem:</label>
            <select
              value={source}
              onChange={onChangeSource}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="interno">Interno (Orçamentos)</option>
              <option value="loja">Loja (Pedidos)</option>
            </select>

            <button
              onClick={onRefresh}
              className="ml-auto bg-gray-800 hover:bg-gray-900 text-white text-xs px-3 py-2 rounded"
              disabled={loading}
              title="Atualizar dados (ignora cache)"
            >
              {loading ? "Atualizando..." : "Atualizar"}
            </button>
          </div>

          {/* Table */}
          <div className="w-full">
            {source === "interno" ? (
              <OrcamentosTable
                data={pagedData}
                loading={loading}
                onOpenStatusModal={openStatusModal}
                onEdit={onEdit}
                onPdf={onPdfOrcamento}
              />
            ) : (
              <PedidosLojaTable
                data={pagedData}
                loading={loading}
                onDetails={onDetailsLoja}
              />
            )}

            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={PAGE_SIZE}
              onPrev={prevPage}
              onNext={nextPage}
              onGoTo={goToPage}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
  user: state.auth.user,
  error: state.auth.error,
});

export default connect(mapStateToProps)(OrcamentosUnifiedPage);
